/**
 * tool-search — the deferred-tool rail (TOOL_SEARCH_V1): our own search, on every lane.
 *
 * THE PROBLEM. A large tool surface is paid for on every turn whether or not the
 * model ever reaches for it: schemas sit in the cached prefix, and the prefix is
 * re-read every round. Deferral withholds a schema until the model asks for it.
 *
 * THIS MODULE is that affordance built out of ordinary tool calls. A small resident
 * tool (`ToolSearch`) takes a query, and its RESULT loads the matched tools. How a
 * load reaches the wire is the lane's business, and there are two shapes:
 *
 *   by reference — Anthropic models that accept `defer_loading` (the transport's
 *     `loadByReference`). Every deferred tool rides `tools` on every request with
 *     `defer_loading: true`, the result is `tool_reference` blocks, and the API
 *     expands each reference where it sits in the messages. The tools array never
 *     changes, so a load costs no cache write in front of it. Anthropic's own
 *     server-side search wrote its pieces into OUR history, and every new placement
 *     it invented for them wedged a task (09-16, 09-22, 10-10); ours writes nothing
 *     we did not send. `transports/anthropic-tool-references.ts` keeps every loaded
 *     tool referenced on every request.
 *   by schema — every other lane: chat-completions (GLM / DeepSeek / Grok / Kimi),
 *     Gemini, OpenAI Responses, and Anthropic models without `defer_loading`. The
 *     result carries the matched tools' full JSON Schemas, and from then on those
 *     tools ride the provider tools array on every request.
 *
 * The journal holds both forms of one result (the schema text, and the names as
 * `toolReferences`), so a session that changes lanes reads its loads either way.
 * The model reads the same index and the same `select:` grammar on every lane.
 *
 * ONE MORE RAIL EXISTS, decided per model by `resolveToolSearchRail`: OpenAI's hosted
 * search (OPENAI_TOOL_SEARCH_V1). Both read the SAME `RawToolOptions.deferrable`
 * marker, which is the one registry (`deferrable-ring.ts` on the product side), so
 * they cannot disagree about which tools are deferred.
 *
 * FOUR PROPERTIES THIS RAIL PRESERVES, each of them a way it could have gone wrong:
 *
 *   1. ADDITIVE-ONLY. The loaded set never shrinks inside a session. A tool the
 *      model loaded on turn 3 is still callable on turn 30, and a recycle rebuilds
 *      the set by replaying the journal rather than starting empty. Nothing this
 *      module does can un-register a tool.
 *   2. DISPATCH IS NEVER GATED. Withholding is a WIRE-SHAPE decision only. The raw
 *      catalog handed to the dispatcher always holds every tool, so a model that
 *      calls a deferred tool by name — from a stale schema, from `execute_code`,
 *      from a resumed transcript — still reaches a real implementation instead of
 *      "Unknown tool".
 *   3. ZERO STANDING COST WHEN THERE IS NOTHING TO FIND. With the flag off or an
 *      empty deferred ring, `ToolSearch` is never registered, and by schema it also
 *      leaves the wire once every deferred tool is loaded. By reference it stays:
 *      dropping it would move the tools prefix, and a deferred tool is only legal
 *      beside the search that loads it.
 *   4. LOADS APPEND. By schema, newly loaded schemas go at the END of the tools
 *      array, never at their catalog position. Providers render tools as a prefix
 *      (Anthropic literally puts them first), so appending keeps every previously-sent
 *      byte at the same offset and costs ONE cache write per load instead of one per
 *      load plus a reshuffle of everything downstream. By reference the tools array
 *      does not move at all.
 *
 * PURITY: every export here is pure except `makeToolSearchTool`'s returned
 * `execute` (which mutates the session's loaded set and calls the persistence
 * hook). No I/O, no clock, no provider SDK — the module is importable from the
 * engine, which may not reach into `transports/`.
 */
import { z } from 'zod';
import { toToolJsonSchema } from '../contract/tool-json-schema.js';
import { modelSupportsToolSearch, resolveCapabilities, type ModelProvider } from '../contract/model-capabilities.js';
import type { RawToolOptions } from '../tools/tool-types.js';
import { openaiToolSearchEnabled, toolDeferEnabled, toolDeferWideEnabled, toolSearchEnabled } from '../policy/feature-flags.js';
import { COMPOSIO_TOOL_PREFIX } from '../policy/engine-constants.js';

/**
 * The resident search tool's name. Deliberately NOT prefixed (`mcp__…`): the
 * capability gate keys off prefixes, and this tool is pure metadata — it reads
 * schemas the process already holds and reaches nothing outside it.
 */
export const TOOL_SEARCH_TOOL_NAME = 'ToolSearch';

/** Default cap on how many tools one query may load. Mirrors the harness default. */
export const TOOL_SEARCH_DEFAULT_MAX_RESULTS = 5;

/** Hard cap per index entry, so a verbose tool description can't bloat the prefix. */
const INDEX_DESCRIPTION_MAX_CHARS = 120;

/** Which rail carries deferral for a given model. */
export type ToolSearchRail = 'native' | 'client' | 'none';

/**
 * Decide which deferral rail a model rides. THE single routing decision — never
 * hand-list models at a call site.
 *
 *   'native' — OpenAI's hosted search (OPENAI_TOOL_SEARCH_V1 with TOOL_DEFER_V1, on
 *              a `supportsToolSearch` GPT row). This module then does NOTHING; the
 *              Responses transport owns the shaping.
 *   'client' — this module's rail, our ToolSearch: every other lane, Claude
 *              included. On Claude it loads by reference (see the module docblock).
 *   'none'   — no deferral (TOOL_SEARCH_V1 off, or nothing marked deferrable).
 *
 * Claude never rides a provider's own search: Anthropic's server-side search wrote
 * blocks into our history that the API then refused in shapes nobody could
 * foresee. TOOL_DEFER_V1 therefore selects nothing on Claude; it pairs only with
 * OPENAI_TOOL_SEARCH_V1. Note the asymmetry that keeps a rail choice safe:
 * mis-routing to `none` costs only the saving, while double-deferring the same tool
 * on two rails would hide it from the wire AND from the search at once.
 */
export function resolveToolSearchRail(model: string, deferredCount: number): ToolSearchRail {
  if (deferredCount <= 0) return 'none';
  if (openaiHostedSearch(model)) return 'native';
  if (toolSearchEnabled()) return 'client';
  return 'none';
}

/** OpenAI's hosted tool search: both of its switches on, a GPT row that accepts it. */
function openaiHostedSearch(model: string): boolean {
  return (
    toolDeferEnabled() &&
    openaiToolSearchEnabled() &&
    resolveCapabilities(model).provider === 'openai' &&
    modelSupportsToolSearch(model)
  );
}

// ── The wide policy (TOOL_DEFER_WIDE_V1) ────────────────────────────────────

/**
 * Is the wide policy in force? Its flag is a POLICY over the two rails, not a rail of
 * its own, so it means nothing while both rails are off.
 */
export function wideDeferralActive(): boolean {
  return toolDeferWideEnabled() && (toolSearchEnabled() || toolDeferEnabled());
}

/**
 * Turn the product's `deferrableWide` proposals into `deferrable` marks while the wide
 * policy is in force. ONE seam, the head of `attachToolSearch`, so both rails, the
 * index and the dispatcher read the same widened set. Copies, never mutates: the
 * product shares tool objects with the subagent toolset, which must not widen.
 *
 * Returns the input BY REFERENCE when the policy is off or nothing is marked, so the
 * flag-off path is byte-identical and allocation-free.
 */
export function widenDeferral(tools: RawToolOptions[]): RawToolOptions[] {
  if (!wideDeferralActive()) return tools;
  if (!tools.some((t) => t.deferrableWide === true && t.deferrable !== true)) return tools;
  return tools.map((t) => (t.deferrableWide === true && t.deferrable !== true ? { ...t, deferrable: true } : t));
}

/** How the deferred-tool index lists a tool: its name alone, or name plus one sentence. */
export type ToolIndexStyle = 'names' | 'summaries';

/**
 * THE PER-PROVIDER SWITCH for the index the wide policy renders. Anthropic models get
 * names only: they read a tool's purpose off its name well, and a keyword search still
 * matches descriptions. Everyone else keeps name plus one
 * sentence until an eval says names alone are enough for them. A full record, so a new
 * provider cannot land without a decision here.
 */
const TOOL_INDEX_STYLE: Readonly<Record<ModelProvider, ToolIndexStyle>> = {
  anthropic: 'names',
  openai: 'summaries',
  google: 'summaries',
  fireworks: 'summaries',
  xai: 'summaries',
  deepseek: 'summaries',
};

export function toolIndexStyle(model: string): ToolIndexStyle {
  return TOOL_INDEX_STYLE[resolveCapabilities(model).provider] ?? 'summaries';
}

// ── The index the model always sees ─────────────────────────────────────────

/**
 * A tool's first description sentence, whitespace-collapsed and hard-capped. The
 * index trades completeness for size on purpose: it exists so the model knows a
 * capability EXISTS and can name it, not so it can call it without loading.
 */
function indexSummary(description: string): string {
  const flat = description.trim().replace(/\s+/g, ' ');
  const sentence = /^(.*?[.!?])(?:\s|$)/.exec(flat)?.[1] ?? flat;
  return sentence.length > INDEX_DESCRIPTION_MAX_CHARS
    ? `${sentence.slice(0, INDEX_DESCRIPTION_MAX_CHARS - 1).trimEnd()}…`
    : sentence;
}

/**
 * How to load a tool, per rail. The NAMES are the same on both — what differs is
 * the verb, because the two rails expose different search mechanisms.
 *
 * OpenAI's hosted search (the native rail) looks a tool up by its name and has no
 * `select:` grammar and no result for us to render, so telling the model about one
 * would be describing a tool the wire does not carry.
 */
function loadInstructions(rail: ToolSearchRail, count: number, style: ToolIndexStyle): string {
  const preamble =
    `These ${count} tools exist and work, but their full definitions are not loaded ` +
    `right now. `;
  return rail === 'native'
    ? preamble +
        'To use one, search the tool catalog for it first, by its exact name from this list. ' +
        'The tools it finds are loaded for you and stay callable for the rest of this session.'
    : preamble +
        `You cannot call them until you load them: calling one directly fails. To load, ` +
        `call ${TOOL_SEARCH_TOOL_NAME} with either \`select:<name>,<name>\` for exact tools, ` +
        `or plain keywords to search ${style === 'names' ? 'their names and descriptions' : 'this list'}. ` +
        `The result returns their full parameter ` +
        `schemas, and from then on they are callable for the rest of this session.`;
}

/**
 * Render the deferred-tool index — the block that rides the system prompt as the
 * `tool_search` instruction layer.
 *
 * WHY EVERY RAIL GETS ONE. A model cannot search for a capability it does not know
 * exists. A provider's own search still needs the MODEL to author the query, out of
 * a catalog it was never shown otherwise. That is not discovery, it is guessing, and
 * it is how `present_output` went silent on prod for three days while the wire
 * carried it the whole time. Anthropic's own
 * guidance says to name the categories in the system prompt; Hermes ships the same
 * listing for the same reason. ~800 tokens of names in the cached prefix buys back
 * ~9,200 tokens of schemas AND the model knowing what it has.
 *
 * CACHE CONTRACT: the output is a pure function of the deferred tools' names and
 * descriptions in catalog order, plus the rail. It carries NO per-turn state — not
 * the loaded set, not a count of remaining tools, not a timestamp — so the bytes
 * change only when the deferred SET changes, which is what lets it sit inside the
 * cached prefix. Putting "you have loaded 3 of 20" here would rewrite the whole
 * system prompt on every load and cost far more than it explains.
 *
 * Returns '' for an empty set, so the layer is dropped and the prompt is
 * byte-identical to a build with no deferral at all.
 */
export function renderToolSearchIndex(
  deferred: readonly RawToolOptions[],
  rail: ToolSearchRail = 'client',
  style: ToolIndexStyle = 'summaries',
): string {
  if (deferred.length === 0) return '';
  const lines = deferred.map((t) =>
    style === 'names' && !NAMES_NEED_A_SENTENCE.has(t.name)
      ? t.name
      : `${style === 'names' ? '' : '- '}${t.name}: ${indexSummary(t.description)}`,
  );
  // Only the wide policy ever defers a Composio tool, so the line is absent from every
  // index built without it.
  const connectedApps = deferred.some((t) => t.name.startsWith(COMPOSIO_TOOL_PREFIX)) ? [CONNECTED_APPS_LINE, ''] : [];
  return [
    `# Additional tools (load before use)`,
    '',
    loadInstructions(rail, deferred.length, style),
    '',
    ...connectedApps,
    ...lines,
  ].join('\n');
}

/**
 * The Composio search tool, by its FULL name. A line once named it bare
 * (`COMPOSIO_SEARCH_TOOLS`), a model copied that into `select:COMPOSIO_SEARCH_TOOLS`, and
 * an exact-only `select:` answered "No tool matched" (staging drill, 30 Sep).
 */
const COMPOSIO_SEARCH_TOOL_NAME = `${COMPOSIO_TOOL_PREFIX}COMPOSIO_SEARCH_TOOLS`;

/**
 * The one category a names-only list cannot convey: the Composio tool names say
 * "composio", not "email" or "calendar". Static, never per-user: a pure function of
 * whether any Composio tool is deferred. The wide policy keeps the search, the execute and
 * the workbench tools resident (most tasks call them, deferrable-ring.ts
 * WIDE_RESIDENT_TOOLS), so the line sends the model to the resident search first, and
 * leaves the deferred rest for when a result names one.
 */
const CONNECTED_APPS_LINE =
  `${COMPOSIO_TOOL_PREFIX}*: more tools for the user's connected apps (email, calendar, chat, documents, CRM). ` +
  `Start with ${COMPOSIO_SEARCH_TOOL_NAME}, which is already loaded; load one of these only when a result names it.`;

/**
 * Built-ins whose names do not say what they do, so the names-only list keeps their
 * one sentence. An exact set: every other name stays bare.
 */
const NAMES_NEED_A_SENTENCE: ReadonlySet<string> = new Set(['Weave', 'Ability']);

// ── Query matching ──────────────────────────────────────────────────────────

/** Split a query into lowercased word tokens (alphanumerics + underscore). */
function tokenize(q: string): string[] {
  return q.toLowerCase().split(/[^a-z0-9_]+/i).filter(Boolean);
}

/**
 * Rank one tool against the query tokens. Name hits outweigh description hits
 * because the index only ever showed the model a name plus one sentence — ranking
 * mostly on description would surface tools whose advertised line the model never
 * read. Returns 0 for no match at all (the caller drops those).
 */
function score(tool: RawToolOptions, tokens: readonly string[]): number {
  const name = tool.name.toLowerCase();
  const description = tool.description.toLowerCase();
  let total = 0;
  for (const token of tokens) {
    if (name.includes(token)) total += 10;
    if (description.includes(token)) total += 1;
  }
  return total;
}

/**
 * The catalog name a requested name means: the exact name (any case), else the ONE name
 * that ends in `__<requested>`. Models drop a namespace prefix they read in the index
 * (`select:computer_click` for `mcp__browser-control__computer_click`, prod 10-10;
 * `COMPOSIO_SEARCH_TOOLS`, staging 30 Sep), and an exact-only match answered "No tool
 * matched" and cost a second search. Two tools sharing the tail resolve to neither.
 */
function resolveToolName(requested: string, names: readonly string[]): string | undefined {
  const want = requested.toLowerCase();
  const exact = names.find((n) => n.toLowerCase() === want);
  if (exact !== undefined) return exact;
  const byTail = names.filter((n) => n.toLowerCase().endsWith(`__${want}`));
  return byTail.length === 1 ? byTail[0] : undefined;
}

/** The names a `select:` query asks for, as written. */
function selectedNames(raw: string): string[] {
  return raw.slice('select:'.length).split(',').map((n) => n.trim()).filter(Boolean);
}

/**
 * Resolve a query against the deferred set.
 *
 * THREE FORMS, in precedence order:
 *   `select:A,B`  — exact names (or a name's unique tail after `__`), no ranking, no
 *                   cap. The model asking for a specific tool it read in the index must
 *                   always get exactly that tool; silently returning a "better match"
 *                   would strand it.
 *   `+kw rest`    — require `kw` as a substring of the NAME, rank the survivors by
 *                   the remaining tokens.
 *   `kw kw`       — plain ranking over name + description.
 *
 * Results are capped (`maxResults`) and returned in descending score, ties broken
 * by catalog order so the same query is always answered the same way. Pure.
 */
export function matchDeferredTools(
  query: string,
  deferred: readonly RawToolOptions[],
  maxResults: number = TOOL_SEARCH_DEFAULT_MAX_RESULTS,
): RawToolOptions[] {
  const raw = query.trim();
  if (!raw) return [];

  if (raw.toLowerCase().startsWith('select:')) {
    const names = deferred.map((t) => t.name);
    const wanted = new Set(selectedNames(raw).map((n) => resolveToolName(n, names)));
    return deferred.filter((t) => wanted.has(t.name));
  }

  let pool = deferred;
  let rest = raw;
  const required = /^\+(\S+)\s*/.exec(raw);
  if (required) {
    const needle = required[1].toLowerCase();
    pool = deferred.filter((t) => t.name.toLowerCase().includes(needle));
    rest = raw.slice(required[0].length);
  }

  const tokens = tokenize(rest);
  // `+kw` alone (no ranking terms left) is a pure filter — return the survivors in
  // catalog order rather than dropping everything for scoring 0.
  if (tokens.length === 0) return pool.slice(0, Math.max(0, maxResults));

  return pool
    .map((tool, index) => ({ tool, index, points: score(tool, tokens) }))
    .filter((r) => r.points > 0)
    .sort((a, b) => (b.points !== a.points ? b.points - a.points : a.index - b.index))
    .slice(0, Math.max(0, maxResults))
    .map((r) => r.tool);
}

// ── Result rendering ────────────────────────────────────────────────────────

/**
 * Serialize matched tools into the block the model reads as a tool definition.
 *
 * The shape mirrors the provider's own tool encoding (one JSON object per line
 * inside a `<functions>` fence) so the model parses it with machinery it already
 * has, rather than a bespoke format it has to be taught. Schemas come from
 * `toToolJsonSchema` — the #2586 serializer — so a loaded schema is byte-identical
 * to the one the tool would have shipped had it been resident. Two sources here
 * would mean the model learns one shape and the wire later sends another.
 */
export function renderToolSchemas(tools: readonly RawToolOptions[]): string {
  const entries = tools.map((t) =>
    `<function>${JSON.stringify({
      description: t.description,
      name: t.name,
      parameters: toToolJsonSchema(t.parameters),
    })}</function>`,
  );
  return `<functions>\n${entries.join('\n')}\n</functions>`;
}

/**
 * The line that tells the model a load took. It matters as much as the schemas:
 * without it a model that just read a definition has no signal that the NEXT request
 * will carry it, and tends to re-search instead of calling. One sentence, shared by
 * the result and by the Anthropic lane's text form of a reference it cannot send.
 */
export function loadedToolsLine(names: readonly string[]): string {
  return `${names.length} tool${names.length === 1 ? '' : 's'} loaded and callable from now on: ${names.join(', ')}.`;
}

// ── The session's loaded set ────────────────────────────────────────────────

/**
 * The per-session record of which deferred tools have been loaded, in FIRST-LOAD
 * order. Order is load order, not catalog order, because that is what a journal
 * replay reproduces exactly — so a recycled runner rebuilds a byte-identical tools
 * array instead of one that merely holds the same set.
 *
 * PER-SESSION BY CONSTRUCTION: created by `createToolSearchState`, never a module
 * const. One sandbox process runs many sessions; a module-level set would let one
 * task's search results widen another task's wire surface.
 */
export interface ToolSearchState {
  /** Names in first-load order. Append-only for the life of the session. */
  readonly order: string[];
  /** Membership index over `order`. */
  readonly loaded: Set<string>;
  /** The ToolSearch call that FIRST loaded each name, where known (a load recorded before this field has none). */
  readonly loadedBy: Map<string, string>;
}

/** One loaded tool, and the ToolSearch call that first loaded it (absent for an older record). */
export interface LoadedTool {
  name: string;
  by?: string;
}

/** A fresh state, optionally seeded from a journal replay (recycle). */
export function createToolSearchState(seed: readonly (string | LoadedTool)[] = []): ToolSearchState {
  const state: ToolSearchState = { order: [], loaded: new Set(), loadedBy: new Map() };
  for (const s of seed) {
    if (typeof s === 'string') recordLoaded(state, s);
    else recordLoaded(state, s.name, s.by);
  }
  return state;
}

/** Append one name if new, with the call that loaded it. Returns true when the set actually grew. */
export function recordLoaded(state: ToolSearchState, name: string, by?: string): boolean {
  if (state.loaded.has(name)) return false;
  state.loaded.add(name);
  state.order.push(name);
  if (by !== undefined) state.loadedBy.set(name, by);
  return true;
}

/** The loaded set in first-load order, each with the call that first loaded it. */
export function loadedTools(state: ToolSearchState): LoadedTool[] {
  return state.order.map((name) => {
    const by = state.loadedBy.get(name);
    return by === undefined ? { name } : { name, by };
  });
}

// ── Wire shaping ────────────────────────────────────────────────────────────

/**
 * Shape one request's provider tools array: drop the deferred-and-not-yet-loaded
 * schemas, append the loaded ones, and drop `ToolSearch` itself when it has
 * nothing left to find.
 *
 * INDEX ALIGNMENT is the load-bearing assumption. Every transport's `buildTools`
 * is a `raw.map(…)`, so `providerTools[i]` is the wire shape of `tools[i]`. That
 * is what lets this stay provider-neutral: the engine never has to know whether a
 * name lives at `.name` (Anthropic / Gemini / Responses) or `.function.name`
 * (chat-completions). A transport that ever appends a provider-side tool would
 * break the alignment silently, so the lengths are CHECKED and a mismatch degrades
 * to "send everything" — the pre-flag behavior, never a wrong tool list.
 *
 * Returns the input array BY REFERENCE when the rail is inactive, so the flag-off
 * path is byte-identical and allocation-free.
 */
export function shapeToolSearchTools<T>(args: {
  tools: readonly RawToolOptions[];
  providerTools: readonly T[];
  state: ToolSearchState;
  active: boolean;
}): readonly T[] {
  const { tools, providerTools, state, active } = args;
  if (!active) return providerTools;
  if (tools.length !== providerTools.length) return providerTools;

  const deferredByName = new Map<string, T>();
  let hidden = 0;
  for (let i = 0; i < tools.length; i++) {
    if (tools[i].deferrable !== true) continue;
    deferredByName.set(tools[i].name, providerTools[i]);
    if (!state.loaded.has(tools[i].name)) hidden++;
  }

  // Property 3: nothing hidden ⇒ the search tool can only ever answer "no
  // results", so it stops occupying the prefix.
  const dropSearchTool = hidden === 0;
  const resident = providerTools.filter(
    (_, i) =>
      tools[i].deferrable !== true && !(dropSearchTool && tools[i].name === TOOL_SEARCH_TOOL_NAME),
  );

  // Loaded tools APPEND in load order (see the module docblock, property 4). A
  // name with no current definition is a tool that vanished between sessions (a
  // retired group, a flag flipped off) — skip it. `announceVanishedTools` tells the
  // model it is gone; silently sending a stale schema would not.
  const loaded = state.order
    .map((name) => deferredByName.get(name))
    .filter((t): t is T => t !== undefined);

  return [...resident, ...loaded];
}

// ── Set-change announcement ─────────────────────────────────────────────────

/**
 * The note the model gets when tools it had LOADED are gone from the catalog — an
 * MCP server that disconnected between turns, a flagged group switched off.
 *
 * SCOPED TO THE LOADED SET ON PURPOSE. A deferred tool the model never loaded can
 * vanish silently: the index is re-rendered from the CURRENT set every turn, so the
 * next prompt simply stops listing it and nothing is ever stale. A LOADED tool is
 * different — the model has its schema, believes it is callable, and will call it.
 * That is the only case a note can fix, so it is the only case that gets one.
 *
 * WHY A NOTE AND NOT A REBUILT INDEX: the index lives in the system prompt, the
 * anchor of the prefix cache. Rewriting it to delete three names would bust the
 * whole cached prefix; a one-line note in the message stream costs a few tokens and
 * says the same thing.
 *
 * Returns undefined when nothing vanished, so the caller writes nothing at all.
 */
export function announceVanishedTools(
  loaded: readonly string[],
  catalog: readonly string[],
): string | undefined {
  const live = new Set(catalog);
  const gone = loaded.filter((n) => !live.has(n));
  if (gone.length === 0) return undefined;
  return (
    `${gone.length} previously loaded tool${gone.length === 1 ? ' is' : 's are'} no longer ` +
    `available and can no longer be called: ${gone.join(', ')}.`
  );
}

// ── The tool itself ─────────────────────────────────────────────────────────

/** The resident tools a query names: each `select:` name, or a keyword query that is exactly one name. */
function residentNamed(query: string, resident: readonly string[]): string[] {
  const raw = query.trim();
  const asked = raw.toLowerCase().startsWith('select:') ? selectedNames(raw) : [raw];
  return [...new Set(asked.map((n) => resolveToolName(n, resident)).filter((n): n is string => n !== undefined))];
}

/**
 * Build the resident `ToolSearch` tool.
 *
 * The description and schema are kept deliberately small: this tool's whole
 * purpose is to be cheap enough to sit in the prefix forever, and every sentence
 * here is paid for on every turn of every session. The index block (the system
 * layer) carries the tool NAMES, so this description only has to explain the query
 * grammar.
 *
 * `getDeferred` is a THUNK, not an array. The tool is constructed while the
 * catalog it searches is still being assembled (it is a member of that catalog),
 * and on the product side the catalog is also wrapped after construction by the
 * creations ledger — so binding the array eagerly would capture a stale,
 * pre-wrapping copy.
 */
export function makeToolSearchTool(deps: {
  getDeferred: () => readonly RawToolOptions[];
  /**
   * The names of the tools already on the wire. A query naming one of them gets "already
   * loaded, call it directly" instead of a miss: on prod a model asked for resident tools
   * (announce_preview, Skill, computer_do) and each miss cost it a second round trip.
   */
  getResident?: () => readonly string[];
  /**
   * The lane loads by reference (Claude). There every loaded tool is already referenced in
   * every request (its first-load result, or the re-made pair: anthropic-tool-references.ts),
   * and each reference costs its full definition again (count_tokens, 10-10). So a result
   * references only the tools it loads NEW, and a search for loaded tools alone is answered
   * in text. Never both in one result: a reference beside text is a 400.
   */
  byReference?: boolean;
  state: ToolSearchState;
  /**
   * Persist newly loaded names (journal). AWAITED before the result returns: a
   * turn can pause for a human immediately after a load, and a recycle that came
   * back without the record would narrow the model's hands mid-task.
   */
  onLoad?: (names: readonly string[], by?: string) => void | Promise<void>;
}): RawToolOptions {
  return {
    name: TOOL_SEARCH_TOOL_NAME,
    description:
      'Load the full definitions of tools listed as not-yet-loaded, so you can call them. ' +
      'Query forms: "select:Name,OtherName" loads those exact tools; "+word rest" requires ' +
      '"word" in the tool name and ranks the rest; anything else is a keyword search over ' +
      'names and descriptions. The result carries the matched tools\' parameter schemas, ' +
      'and they stay callable for the rest of the session.',
    // L2 (TOOL_CONCURRENCY_CLASSES_V1): a catalog read. Its one write is a union into the
    // loaded set, so two searches in one batch load the same tools either way; they append
    // in the order their loads were journaled, which is the order a recycle replays.
    concurrency: 'shared',
    parameters: z.object({
      query: z
        .string()
        .describe('"select:Name,Name" for exact tools, or keywords to search for a capability.'),
      max_results: z
        .number()
        .int()
        .optional()
        .describe(`Maximum tools to load from a keyword search (default ${TOOL_SEARCH_DEFAULT_MAX_RESULTS}).`),
    }),
    // The result is an envelope so the dispatcher can lift the loaded names beside the
    // text (tool-dispatcher.ts extractContentBlocks): the journal keeps both, and each
    // lane renders the one it loads by. A miss is a plain string, which loads nothing.
    returnsContentBlocks: true,
    execute: async (args: unknown, context?: { toolCallId?: string }) => {
      const input = (args ?? {}) as { query?: unknown; max_results?: unknown };
      const query = typeof input.query === 'string' ? input.query : '';
      const max =
        typeof input.max_results === 'number' && Number.isFinite(input.max_results)
          ? Math.trunc(input.max_results)
          : TOOL_SEARCH_DEFAULT_MAX_RESULTS;

      const deferred = deps.getDeferred();
      const matched = matchDeferredTools(query, deferred, max);
      // A query that names a resident tool means that tool: answered before a keyword
      // search can load a lookalike, and before a select: miss can cost a second search.
      const resident = residentNamed(query, deps.getResident?.() ?? []);
      if (resident.length > 0 && (matched.length === 0 || !query.trim().toLowerCase().startsWith('select:'))) {
        return `Already loaded and callable, call ${resident.length === 1 ? 'it' : 'them'} directly: ${resident.join(', ')}.`;
      }
      if (matched.length === 0) {
        const names = deferred
          .filter((t) => !deps.state.loaded.has(t.name))
          .map((t) => t.name);
        // A miss is a successful call that loads nothing, so the model carries on without
        // the capability it went looking for and no error reaches anyone. The QUERY is
        // what makes it debuggable after the fact: a recurring miss means a tool's name
        // and the words the model reaches for have drifted apart.
        console.warn(`[TOOL_SEARCH_EMPTY] a tool search matched nothing: query=${JSON.stringify(query)}`);
        return names.length === 0
          ? 'No tools left to load: every deferred tool is already loaded and callable.'
          : `No tool matched "${query}". Loadable tools: ${names.join(', ')}.`;
      }

      // By reference, only the tools this search loads new. A tool Anthropic's own search
      // loaded (before our rail) was never recorded as loaded, so it loads here like any other.
      const shown = deps.byReference ? matched.filter((t) => !deps.state.loaded.has(t.name)) : matched;
      if (shown.length === 0) {
        return `Already loaded and callable, call ${matched.length === 1 ? 'it' : 'them'} directly: ${matched.map((t) => t.name).join(', ')}.`;
      }

      // The call that first loads a tool is recorded with it: on Claude its result is the
      // reference that tool keeps for the rest of the task (anthropic-tool-references.ts).
      const by = context?.toolCallId;
      const fresh = matched.map((t) => t.name).filter((n) => recordLoaded(deps.state, n, by));
      if (fresh.length > 0) await deps.onLoad?.(fresh, by);

      // The RESULT is the whole mechanism: by schema the text carries the definitions
      // (and the "now callable" line), by reference the names do, and the API expands
      // each into its definition where the result sits.
      const names = shown.map((t) => t.name);
      return {
        content: [{ type: 'text', text: [renderToolSchemas(shown), '', loadedToolsLine(names)].join('\n') }],
        toolReferences: names,
      };
    },
  };
}
